import { useState, useCallback, useEffect } from 'react';

import { useAuth } from '../../../hooks/use-auth';
import * as patientsService from '../../../services/api/patients';
import * as ordersService from '../../../services/api/orders';
import {
  PatientManagementState,
  UsePatientManagementReturn,
  CreatePatientFormData,
  PatientSearchFilters,
} from '../types/patient-management.types';
import PatientProps from '../../../entities/patient';

const initialState: PatientManagementState = {
  patients: [],
  totalPages: 1,
  currentPage: 1,
  selectedPatient: null,
  isLoading: false,
  isCreating: false,
  searchFilters: {
    textSearch: '',
    page: 1,
    limit: 10,
  },
  viewMode: 'list',
  error: null,
};

export const usePatientManagement = (customerId?: number): UsePatientManagementReturn => {
  const [state, setState] = useState<PatientManagementState>(initialState);
  const { token } = useAuth();

  const searchPatients = useCallback(
    async (filters: Partial<PatientSearchFilters>) => {
      const updatedFilters = {
        ...initialState.searchFilters,
        ...filters,
        ...(customerId && { customerId }),
      };

      setState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
        searchFilters: updatedFilters,
      }));

      try {
        const response = await patientsService.getPatients(token, updatedFilters);

        setState((prev) => ({
          ...prev,
          patients: response.patients || [],
          totalPages: response.pages || 1,
          currentPage: updatedFilters.page || 1,
          isLoading: false,
        }));
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load patients',
        }));
      }
    },
    [token, customerId],
  );

  const selectPatient = useCallback((patient: PatientProps) => {
    setState((prev) => ({
      ...prev,
      selectedPatient: patient,
    }));
  }, []);

  const clearSelection = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedPatient: null,
    }));
  }, []);

  const setViewMode = useCallback((mode: 'list' | 'create') => {
    setState((prev) => ({
      ...prev,
      viewMode: mode,
      error: null,
    }));
  }, []);

  const createPatient = useCallback(
    async (data: CreatePatientFormData): Promise<PatientProps> => {
      setState((prev) => ({ ...prev, isCreating: true, error: null }));

      try {
        const newPatient = await patientsService.createPatient(token, data);

        if (!newPatient) {
          throw new Error('Failed to create patient');
        }

        setState((prev) => ({
          ...prev,
          isCreating: false,
          selectedPatient: newPatient,
          viewMode: 'list',
        }));

        await searchPatients({ page: 1 });

        return newPatient;
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isCreating: false,
          error: error instanceof Error ? error.message : 'Failed to create patient',
        }));
        throw error;
      }
    },
    [token, searchPatients],
  );

  const assignPatientToOrder = useCallback(
    async (patientId: number, orderId: number): Promise<void> => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        await ordersService.update(token, orderId, { patientId });
        setState((prev) => ({ ...prev, isLoading: false }));
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to assign patient to order',
        }));
        throw error;
      }
    },
    [token],
  );

  const resetModal = useCallback(() => {
    setState({
      ...initialState,
      searchFilters: {
        ...initialState.searchFilters,
        ...(customerId && { customerId }),
      },
    });
  }, [customerId]);

  const loadNextPage = useCallback(() => {
    setState((prev) => {
      if (prev.currentPage < prev.totalPages && !prev.isLoading) {
        const nextPage = prev.currentPage + 1;

        searchPatients({ ...prev.searchFilters, page: nextPage }).catch(() => {
          // Handle pagination error
        });
      }

      return prev;
    });
  }, [searchPatients]);

  const loadPreviousPage = useCallback(() => {
    setState((prev) => {
      if (prev.currentPage > 1 && !prev.isLoading) {
        const prevPage = prev.currentPage - 1;

        searchPatients({ ...prev.searchFilters, page: prevPage }).catch(() => {
          // Handle pagination error
        });
      }

      return prev;
    });
  }, [searchPatients]);

  useEffect(() => {
    const initialSearch = async () => {
      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        const searchParams = {
          page: 1,
          limit: 10,
          ...(customerId && { customerId }),
        };
        const response = await patientsService.getPatients(token, searchParams);

        setState((prev) => ({
          ...prev,
          patients: response.patients || [],
          totalPages: response.pages || 1,
          currentPage: 1,
          isLoading: false,
        }));
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load patients',
        }));
      }
    };

    initialSearch();
  }, [token, customerId]);

  return {
    state,
    actions: {
      searchPatients,
      selectPatient,
      clearSelection,
      setViewMode,
      createPatient,
      assignPatientToOrder,
      resetModal,
      loadNextPage,
      loadPreviousPage,
    },
  };
};
